import { inject } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlSerializer,
  UrlTree,
  CanActivateFn,
  CanActivateChildFn,
  CanMatchFn,
} from '@angular/router'
import { AuthService } from './auth.service'
import { merge, Observable, of } from 'rxjs'
import { filter, first, map, switchMap } from 'rxjs/operators'

export const authGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService)
  const urlSerializer = inject(UrlSerializer)

  authService.redirectUrl = state.url

  const isLoggedIn$ = authService.isLoggedIn$

  const canActivate$ = isLoggedIn$.pipe(filter(Boolean)) as Observable<boolean>

  const isNotLoggedIn = (isLoggedIn) => !isLoggedIn
  const redirectToLogin = () => of(urlSerializer.parse('/login'))
  const cannotActivate$ = isLoggedIn$.pipe(filter(isNotLoggedIn), switchMap(redirectToLogin))

  return merge(canActivate$, cannotActivate$).pipe(first())
}

export const authChildGuard: CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return authGuard(route, state)
}

export const authMatchGuard: CanMatchFn = (route, segments): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService)
  const urlSerializer = inject(UrlSerializer)

  const attemptedUrl = '/' + segments.map((s) => s.path).join('/')

  return authService.isLoggedIn$.pipe(
    first(),
    map((isLoggedIn) => {
      if (!isLoggedIn) {
        const encodedUrl = encodeURIComponent(attemptedUrl)
        return urlSerializer.parse(`/login?redirect=${encodedUrl}`)
      }
      return true
    }),
  )
}
